import { Component } from "react";

const messages = {
  fr: {
    title: "Oups, une erreur est survenue",
    description: "Un problème inattendu est apparu sur cette page. Vous pouvez rafraîchir ou revenir à l'accueil.",
    reload: "Rafraîchir la page",
    home: "Retour à l'accueil",
  },
  en: {
    title: "Oops, something went wrong",
    description: "An unexpected error occurred on this page. You can refresh or return to the home page.",
    reload: "Refresh the page",
    home: "Back to home",
  },
  nl: {
    title: "Oeps, er is een fout opgetreden",
    description: "Er is een onverwacht probleem opgetreden op deze pagina. U kunt de pagina vernieuwen of teruggaan naar de startpagina.",
    reload: "Pagina vernieuwen",
    home: "Terug naar de startpagina",
  },
};

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || null });
  }

  componentDidUpdate(prevProps) {
    // Reset when the protected subtree identity changes (e.g. route / map remount).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, componentStack: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const lang = (typeof window !== "undefined" && (
      localStorage.getItem("matpro_lang_pref")
      || document.documentElement.lang?.slice(0, 2)
    )) || "fr";
    const m = messages[lang] || messages.fr;

    // Only expose technical detail in non-production environments
    const isDev = typeof window !== "undefined" && (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.endsWith(".ngrok-free.app") ||
      window.location.hostname.endsWith(".ngrok.io")
    );
    const detail = isDev ? (this.state.error?.message || String(this.state.error || "")) : null;
    const stack = isDev ? (this.state.componentStack || "") : null;

    return (
      <div className="matpro-container py-16 md:py-24 max-w-lg" data-testid="error-boundary">
        <div className="bg-white border border-matpro-border rounded-xl p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-3xl" aria-hidden>
            ⚠️
          </div>
          <h1 className="font-heading font-black text-2xl text-matpro-anthracite">{m.title}</h1>
          <p className="mt-3 text-sm text-matpro-anthracite-muted">{m.description}</p>
          {(detail || stack) ? (
            <pre
              className="mt-4 max-h-40 overflow-auto rounded-md bg-zinc-50 p-3 text-left text-[11px] text-zinc-600 whitespace-pre-wrap break-words"
              data-testid="error-boundary-detail"
            >
              {detail}
              {stack ? `\n${stack}` : ""}
            </pre>
          ) : null}
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, error: null, componentStack: null });
                window.location.assign(window.location.pathname + window.location.search);
              }}
              className="bg-matpro-green hover:bg-matpro-green-dark text-white font-bold rounded-md min-h-11 px-4"
              data-testid="error-boundary-reload"
            >
              {m.reload}
            </button>
            <a
              href="/"
              className="border border-matpro-border font-semibold rounded-md min-h-11 px-4 inline-flex items-center justify-center text-matpro-anthracite hover:bg-zinc-50"
              data-testid="error-boundary-home"
            >
              {m.home}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
